import { useCallback, useState } from 'react';
import { Box, Divider, Menu, MenuItem, Tooltip, Typography, styled, useTheme } from '@mui/material';
import SvgIcon from '../../components/SvgIcon';
import { ReactComponent as UploadIcon } from '../../assets/icons/upload.svg';
import { ReactComponent as UploadedFileIcon } from '../../assets/icons/40/file_uploaded.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/24/remove.svg';
import { ReactComponent as PhotoIcon } from '../../assets/icons/photo_upload.svg';
import { getThemeColors } from '../../helpers/themeStyles';
import StatusBadge from '../../components/StatusBadge';
import InputField from '../../components/InputField';
import DateField from '../../components/DateField';
import Button from '../../components/Button';
import { formatDate } from '../../helpers/date';
import { CAR_FILE_TYPES, FILE_STATUS_VARIANTS } from '../../constants/file';
import { CAR_STATUS_OPTIONS } from '../../constants/car';
import { getCarStatusLabel } from '../../helpers/status';
import { TOOLTIP_SLOT_PROPS } from '../../constants/tooltip';
import {
  ContentGrid,
  FormSection,
  ActivityToggle,
  DocumentsSection,
  SectionTitle,
  DocumentsGrid,
  DocumentCardWrapper,
  DocumentCard,
  DocumentCardActions,
  DocumentCardLabel,
  DocumentCardDateLabel,
  DocumentUploadButton,
  ActionIconButton,
  SegmentedControlWrapper,
  SegmentedControl,
  SegmentedOption,
  SegmentedFieldLabel,
} from './sharedStyles';

const StatusMenu = styled(Menu)({
  '& .MuiPaper-root': {
    background: '#303030',
    borderRadius: 14,
    marginTop: 4,
    border: '1px solid #494949',
  },
  '& .MuiList-root': {
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    gap: 12,
  },
});

const StatusMenuItem = styled(MenuItem)({
  padding: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const CAR_ENERGY_OPTIONS = [
  { value: 'GASOLINE', label: 'Essence' },
  { value: 'ELECTRIC', label: 'Électrique' },
  { value: 'HYBRID', label: 'Hybride' },
];

const CAR_TRANSMISSION_OPTIONS = [
  { value: 'AUTOMATIC', label: 'Automatique' },
  { value: 'MANUAL', label: 'Manuelle' },
];

const CarDetailsTab = ({
  carFormData,
  setCarFormData,
  carFormErrors,
  setCarFormErrors,
  carDocuments,
  carPhotoDoc,
  onCarUploadClick,
  onFileClick,
  onRemove,
  onStatusClick,
  onSave,
  uploadingType,
  isExpiringSoon,
  hasCarFormChanged,
  isSaving,
}) => {
  const theme = useTheme();
  const colors = getThemeColors(theme);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const statusMenuOpen = Boolean(statusAnchorEl);

  const handleStatusClick = useCallback((event) => {
    setStatusAnchorEl(event.currentTarget);
  }, []);

  const handleStatusMenuClose = useCallback(() => {
    setStatusAnchorEl(null);
  }, []);

  const handleStatusSelect = useCallback(
    (newStatus) => {
      setCarFormData((p) => ({ ...p, status: newStatus }));
      handleStatusMenuClose();
    },
    [setCarFormData, handleStatusMenuClose]
  );

  const clearFieldError = (field) => {
    if (setCarFormErrors) {
      setCarFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };
  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      <ContentGrid>
        <FormSection sx={{ gap: '16px' }}>
          <SectionTitle sx={{ marginBottom: 0 }}>INFORMATIONS VÉHICULE</SectionTitle>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <InputField
              label="Marque"
              value={carFormData.brand}
              onChange={(e) => {
                setCarFormData((p) => ({ ...p, brand: e.target.value }));
                clearFieldError('brand');
              }}
              error={!!carFormErrors?.brand}
              helperText={carFormErrors?.brand}
              fullWidth
              onClear={() => {
                setCarFormData((p) => ({ ...p, brand: '' }));
                clearFieldError('brand');
              }}
            />
            <InputField
              label="Modèle"
              value={carFormData.model}
              onChange={(e) => {
                setCarFormData((p) => ({ ...p, model: e.target.value }));
                clearFieldError('model');
              }}
              error={!!carFormErrors?.model}
              helperText={carFormErrors?.model}
              fullWidth
              onClear={() => {
                setCarFormData((p) => ({ ...p, model: '' }));
                clearFieldError('model');
              }}
            />
            <InputField
              label="Couleur"
              value={carFormData.color}
              onChange={(e) => {
                setCarFormData((p) => ({ ...p, color: e.target.value }));
                clearFieldError('color');
              }}
              error={!!carFormErrors?.color}
              helperText={carFormErrors?.color}
              fullWidth
              onClear={() => {
                setCarFormData((p) => ({ ...p, color: '' }));
                clearFieldError('color');
              }}
            />
            <InputField
              label="Plaque d'immatriculation"
              value={carFormData.plateNumber}
              onChange={(e) => {
                setCarFormData((p) => ({ ...p, plateNumber: e.target.value }));
                clearFieldError('plateNumber');
              }}
              error={!!carFormErrors?.plateNumber}
              helperText={carFormErrors?.plateNumber}
              fullWidth
              onClear={() => {
                setCarFormData((p) => ({ ...p, plateNumber: '' }));
                clearFieldError('plateNumber');
              }}
            />
            <InputField
              label="CV"
              value={carFormData.power}
              onChange={(e) => {
                setCarFormData((p) => ({ ...p, power: e.target.value }));
                clearFieldError('power');
              }}
              error={!!carFormErrors?.power}
              helperText={carFormErrors?.power}
              fullWidth
              onClear={() => {
                setCarFormData((p) => ({ ...p, power: '' }));
                clearFieldError('power');
              }}
            />
            <DateField
              sx={{ width: '100%' }}
              label="Date de référence"
              value={carFormData.registrationDate || null}
              onChange={(date) => {
                setCarFormData((p) => ({
                  ...p,
                  registrationDate: date ? date.toISOString() : '',
                }));
                clearFieldError('registrationDate');
              }}
              onClear={() => {
                setCarFormData((p) => ({ ...p, registrationDate: '' }));
                clearFieldError('registrationDate');
              }}
              error={!!carFormErrors?.registrationDate}
              helperText={carFormErrors?.registrationDate}
              dateFormat="medium"
            />
          </Box>

          <Box>
            <SegmentedFieldLabel>ENERGIE</SegmentedFieldLabel>
            <SegmentedControlWrapper sx={{ width: '100%' }}>
              <SegmentedControl sx={{ width: '100%' }}>
                {CAR_ENERGY_OPTIONS.map((opt) => (
                  <SegmentedOption
                    key={opt.value}
                    $active={carFormData.fuelType === opt.value}
                    sx={{ flex: 1 }}
                    onClick={() => setCarFormData((p) => ({ ...p, fuelType: opt.value }))}
                  >
                    {opt.label}
                  </SegmentedOption>
                ))}
              </SegmentedControl>
            </SegmentedControlWrapper>
          </Box>

          <Box>
            <SegmentedFieldLabel>TRANSMISSION</SegmentedFieldLabel>
            <SegmentedControlWrapper sx={{ width: '100%' }}>
              <SegmentedControl sx={{ width: '100%' }}>
                {CAR_TRANSMISSION_OPTIONS.map((opt) => (
                  <SegmentedOption
                    key={opt.value}
                    $active={carFormData.transmission === opt.value}
                    sx={{ flex: 1 }}
                    onClick={() => setCarFormData((p) => ({ ...p, transmission: opt.value }))}
                  >
                    {opt.label}
                  </SegmentedOption>
                ))}
              </SegmentedControl>
            </SegmentedControlWrapper>
          </Box>

          <Box>
            <ActivityToggle>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.06,
                }}
              >
                STATUT D'ACTIVITÉ
              </Typography>
              <StatusBadge
                active
                variant={carFormData.status}
                sx={{ cursor: 'pointer' }}
                onClick={handleStatusClick}
              >
                {getCarStatusLabel(carFormData.status)}
              </StatusBadge>
            </ActivityToggle>
            <StatusMenu
              anchorEl={statusAnchorEl}
              open={statusMenuOpen}
              onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown')
                  handleStatusMenuClose();
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {CAR_STATUS_OPTIONS.filter((status) => status !== carFormData.status).map(
                (status) => (
                  <StatusMenuItem
                    key={status}
                    onClick={() => handleStatusSelect(status)}
                    disableRipple
                  >
                    <StatusBadge variant={status}>{getCarStatusLabel(status)}</StatusBadge>
                  </StatusMenuItem>
                )
              )}
            </StatusMenu>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={onSave}
              disabled={!hasCarFormChanged || !!isSaving}
              sx={{ width: '100%' }}
            >
              ENREGISTRER
            </Button>
          </Box>
        </FormSection>

        <>
          <Divider orientation="vertical" flexItem />

          <DocumentsSection>
            <SectionTitle>DOCUMENTS</SectionTitle>
            <DocumentsGrid>
              {carDocuments.map((doc) => (
                <DocumentCardWrapper key={doc.id}>
                  {doc.empty ? (
                    <DocumentUploadButton
                      sx={{ opacity: uploadingType === doc.type ? 0.6 : 1 }}
                      onClick={() => onCarUploadClick(doc)}
                    >
                      <UploadIcon />
                    </DocumentUploadButton>
                  ) : (
                    <DocumentCard
                      sx={onFileClick ? { cursor: 'pointer' } : undefined}
                      onClick={() => onFileClick?.(doc)}
                    >
                      <SvgIcon icon={UploadedFileIcon} color={colors.iconOnSurface} size={40} />
                      <Tooltip
                        arrow
                        title={doc.name ?? ''}
                        placement="bottom"
                        slotProps={TOOLTIP_SLOT_PROPS}
                      >
                        <Box
                          sx={{
                            width: 'calc(100% - 24px)',
                            minWidth: 0,
                            overflow: 'hidden',
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {doc.name}
                          </Typography>
                        </Box>
                      </Tooltip>
                      <DocumentCardActions onClick={(e) => e.stopPropagation()}>
                        <Tooltip
                          arrow
                          title="Supprimer"
                          placement="bottom"
                          slotProps={TOOLTIP_SLOT_PROPS}
                        >
                          <ActionIconButton $accentColor="#FF6B6B" onClick={() => onRemove(doc.id)}>
                            <SvgIcon icon={TrashIcon} color="inherit" />
                          </ActionIconButton>
                        </Tooltip>
                        <StatusBadge
                          active
                          variant={doc.status ?? FILE_STATUS_VARIANTS.PENDING}
                          sx={{ cursor: 'pointer' }}
                          onClick={(e) => onStatusClick(e, doc)}
                        />
                      </DocumentCardActions>
                    </DocumentCard>
                  )}
                  <Box>
                    <DocumentCardLabel>{doc.label}</DocumentCardLabel>
                    {doc.date && (
                      <DocumentCardDateLabel>
                        {formatDate(doc.date, { format: 'medium' })}
                        {doc.expiring && isExpiringSoon(doc.date) && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: '#FF6669',
                              marginLeft: 1,
                            }}
                          >
                            Expire bientôt
                          </Typography>
                        )}
                      </DocumentCardDateLabel>
                    )}
                  </Box>
                </DocumentCardWrapper>
              ))}
            </DocumentsGrid>

            <Divider sx={{ my: 3 }} />

            <SectionTitle>PHOTO AVANT DU VÉHICULE</SectionTitle>

            {carPhotoDoc.empty ? (
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  onCarUploadClick({
                    type: CAR_FILE_TYPES.FRONT_PHOTO,
                    expiring: false,
                  })
                }
              >
                <PhotoIcon />
              </Box>
            ) : (
              <Box
                sx={{
                  width: 480,
                  height: 320,
                  position: 'relative',
                  cursor: onFileClick ? 'pointer' : undefined,
                }}
                onClick={() => onFileClick?.(carPhotoDoc)}
              >
                <Box
                  component="img"
                  src={carPhotoDoc.url}
                  alt={carPhotoDoc.label}
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '20px',
                    minWidth: 480,
                    minHeight: 320,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to top, #141414 0%, rgba(20, 20, 20, 0) 100%)',
                    borderRadius: '20px',
                    pointerEvents: 'none',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    bottom: 24,
                    width: '100%',
                    p: '0px 24px',
                    zIndex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {carPhotoDoc.name}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip
                      arrow
                      title="Supprimer"
                      placement="bottom"
                      slotProps={TOOLTIP_SLOT_PROPS}
                    >
                      <ActionIconButton
                        $accentColor="#FF6B6B"
                        onClick={() => onRemove(carPhotoDoc.id)}
                      >
                        <SvgIcon icon={TrashIcon} color="inherit" />
                      </ActionIconButton>
                    </Tooltip>
                    <StatusBadge
                      active
                      variant={carPhotoDoc.status ?? FILE_STATUS_VARIANTS.PENDING}
                      sx={{ cursor: 'pointer' }}
                      onClick={(e) => onStatusClick(e, carPhotoDoc)}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </DocumentsSection>
        </>
      </ContentGrid>
    </Box>
  );
};

export default CarDetailsTab;
